import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, RotateCcw, ChevronDown, MapPin, ExternalLink, FileText, MessageCircle } from "lucide-react";
import { SITE } from "../config/site";

const waLink = (nome, ateneo) =>
  `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
    `Buongiorno, vorrei informazioni sul percorso ${nome} di ${ateneo}.`
  )}`;

const Field = ({ label, value }) =>
  value ? (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</p>
      <p className="mt-1 text-sm text-slate-700 leading-relaxed">{value}</p>
    </div>
  ) : null;

const selectCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-navy outline-none transition-colors focus:border-azure appearance-none";

export const Catalogo = ({ items, kind, testId }) => {
  const [query, setQuery] = useState("");
  const [ateneo, setAteneo] = useState("");
  const [area, setArea] = useState("");
  const [tipo, setTipo] = useState("");
  const [openId, setOpenId] = useState(null);

  const isLaurea = kind === "laurea";
  const tipoLabel = isLaurea ? "Tipologia" : "Livello";

  const aree = useMemo(() => [...new Set(items.map((i) => i.area))].sort(), [items]);
  const tipi = useMemo(
    () => [...new Set(items.map((i) => (isLaurea ? i.tipologia : i.livello)))],
    [items, isLaurea]
  );
  const atenei = useMemo(() => [...new Set(items.map((i) => i.ateneo))], [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (ateneo && i.ateneo !== ateneo) return false;
      if (area && i.area !== area) return false;
      if (tipo && (isLaurea ? i.tipologia : i.livello) !== tipo) return false;
      if (q) {
        const hay = `${i.nome} ${i.classe || ""} ${(i.curricula || []).join(" ")} ${(i.sedi || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, query, ateneo, area, tipo, isLaurea]);

  const reset = () => {
    setQuery("");
    setAteneo("");
    setArea("");
    setTipo("");
  };

  const hasFilters = query || ateneo || area || tipo;

  return (
    <div data-testid={testId}>
      <div className="bg-white rounded-[1.4rem] border border-slate-200 p-5 sm:p-6 mb-8 shadow-[0_20px_50px_-35px_rgba(8,42,89,0.35)]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              data-testid={`${testId}-search`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isLaurea ? "Cerca corso o indirizzo…" : "Cerca master…"}
              className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3.5 py-2.5 text-sm text-navy placeholder:text-slate-400 outline-none transition-colors focus:border-azure"
            />
          </div>
          <select data-testid={`${testId}-filter-ateneo`} value={ateneo} onChange={(e) => setAteneo(e.target.value)} className={selectCls}>
            <option value="">Tutti gli atenei</option>
            {atenei.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select data-testid={`${testId}-filter-area`} value={area} onChange={(e) => setArea(e.target.value)} className={selectCls}>
            <option value="">Tutte le aree</option>
            {aree.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select data-testid={`${testId}-filter-tipo`} value={tipo} onChange={(e) => setTipo(e.target.value)} className={selectCls}>
            <option value="">{isLaurea ? "Tutte le tipologie" : "Tutti i livelli"}</option>
            {tipi.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <p data-testid={`${testId}-count`} className="text-sm font-semibold text-navy/70">
            {filtered.length} {filtered.length === 1 ? "risultato" : "risultati"}
          </p>
          {hasFilters && (
            <button
              data-testid={`${testId}-reset`}
              onClick={reset}
              className="inline-flex items-center gap-2 text-sm font-bold text-azure hover:underline underline-offset-4"
            >
              <RotateCcw size={14} />
              Azzera filtri
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div data-testid={`${testId}-empty`} className="rounded-[1.4rem] border-2 border-dashed border-slate-300 bg-white/60 px-8 py-14 text-center">
          <p className="text-base font-bold text-navy">Nessun percorso trovato</p>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            Prova a modificare la ricerca o ad azzerare i filtri. Se cerchi qualcosa di specifico, contattaci: ti aiutiamo noi.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((item, idx) => {
            const id = `${kind}-${idx}-${item.nome}`;
            const open = openId === id;
            return (
              <div
                key={id}
                data-testid={`${testId}-card-${idx}`}
                className={`bg-white rounded-[1.3rem] border transition-all duration-300 ${
                  open ? "border-azure shadow-[0_28px_60px_-35px_rgba(8,42,89,0.45)]" : "border-slate-200 hover:border-azure/60"
                }`}
              >
                <div className="p-6">
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                      item.ateneo === "eCampus" ? "bg-azure-light text-azure" : "bg-navy text-white"
                    }`}>
                      {item.ateneo === "eCampus" ? "eCampus" : "Link"}
                    </span>
                    {item.classe && (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-500">
                        {item.classe}
                      </span>
                    )}
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-500">
                      {isLaurea ? item.tipologia : item.livello}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold tracking-tight text-navy leading-snug">
                    {item.nome}
                  </h3>
                  <p className="mt-2 text-[13px] text-slate-500">
                    {[item.durata, item.edizione, item.modalita].filter(Boolean).join(" · ")}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <button
                      data-testid={`${testId}-detail-${idx}`}
                      onClick={() => setOpenId(open ? null : id)}
                      className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-4.5 py-2 px-5 text-[13px] font-bold text-navy transition-colors hover:border-azure hover:text-azure"
                    >
                      Scopri il percorso
                      <ChevronDown size={15} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                    </button>
                    <a
                      href={waLink(item.nome, item.ateneo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${testId}-wa-${idx}`}
                      className="inline-flex items-center gap-2 rounded-full bg-azure px-5 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[#1C446E]"
                    >
                      <MessageCircle size={15} />
                      Richiedi informazioni
                    </a>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        <Field label="Ateneo" value={item.ateneo} />
                        {isLaurea && <Field label="Classe di laurea" value={item.classe} />}
                        <Field label={isLaurea ? "Tipologia" : "Livello"} value={isLaurea ? item.tipologia : item.livello} />
                        <Field label="Durata" value={item.durata} />
                        <Field label="Edizione" value={item.edizione} />
                        <Field label="CFU" value={item.cfu} />
                        <Field label="Modalità didattica" value={item.modalita} />
                        <Field label="Requisiti e accesso" value={item.accesso || item.requisiti} />
                        <Field label="Scadenza iscrizioni" value={item.scadenza} />
                        {item.sedi?.length > 0 && (
                          <div className="sm:col-span-2">
                            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Sedi del corso</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {item.sedi.map((s) => (
                                <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-navy">
                                  <MapPin size={12} className="text-azure" />
                                  {s}
                                </span>
                              ))}
                            </div>
                            {item.sedi_prova && (
                              <p className="mt-2 text-xs text-slate-500 leading-relaxed">{item.sedi_prova}</p>
                            )}
                          </div>
                        )}
                        {item.curricula?.length > 0 && (
                          <div className="sm:col-span-2">
                            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Curricula e indirizzi</p>
                            <ul className="mt-2 space-y-1.5">
                              {item.curricula.map((c) => (
                                <li key={c} className="text-sm text-slate-700">• {c}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.nota && (
                          <p className="sm:col-span-2 text-xs text-slate-500 italic">{item.nota}</p>
                        )}
                        <p className="sm:col-span-2 text-xs text-slate-500 leading-relaxed">
                          Requisiti dettagliati, piano di studi, eventuali attività obbligatorie in presenza
                          (esami, laboratori, tirocini) e condizioni economiche sono indicati nella pagina ufficiale del percorso.
                        </p>
                        <div className="sm:col-span-2 flex flex-wrap gap-3 pt-1">
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${testId}-official-${idx}`}
                            className="inline-flex items-center gap-2 text-[13px] font-bold text-azure hover:underline underline-offset-4"
                          >
                            <ExternalLink size={14} />
                            Pagina ufficiale del percorso
                          </a>
                          {item.sua && (
                            <a
                              href={item.sua}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-[13px] font-bold text-slate-500 hover:text-azure"
                            >
                              <FileText size={14} />
                              Scheda SUA (PDF)
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
